<template>
  <fieldset class="password-fields-container">
    <div
      v-if="showOldPassword"
      :class="rowClass"
    >
      <label
        class="control-label"
        :class="lblClass"
      >Old Password*</label>
      <password-field
        v-model="oldPasswordValue"
        placeholder="Please input old password"
        :class="fieldClass"
        required
      />
    </div>

    <div :class="rowClass">
      <label
        class="control-label"
        :class="lblClass"
      >{{ showOldPassword ? 'New Password' : 'Password' }}{{ required ? '*' : '' }}</label>
      <password-field
        v-model="passwordValue"
        placeholder="Please input new password"
        :class="!isPasswordMatched ? `${fieldClass} invalid` : fieldClass"
        :required="required"
        @change="updateValidation"
        @keyup="updateValidation"
      />
    </div>

    <div :class="rowClass">
      <label
        class="control-label"
        :class="lblClass"
      >Confirm Password{{ required ? '*' : '' }}</label>
      <password-field
        v-model="confirmPasswordValue"
        placeholder="Please input new password again"
        :class="!isPasswordMatched ? `${fieldClass} invalid` : fieldClass"
        :required="required"
        @change="updateValidation"
        @keyup="updateValidation"
      />
    </div>

    <div
      v-if="!!password"
      :class="rowClass"
    >
      <label :class="lblClass">&nbsp;</label>
      <div
        :class="fieldClass"
        class="condition-case-wrapper"
      >
        <div class="condition-case">
          <i :class="isPasswordMatched ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">Password should be matched</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isLengthCorrect ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">The minimum password length is 15</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isIncludeNumber ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">Includes at least one number</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isIncludeUppercase ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">Includes at least one uppercase</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isIncludeLowercase ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">Includes at least one lowercase</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isNotIncludeSpace ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">No spaces allowed</span>
        </div>
        <div class="condition-case mt-5">
          <i :class="isSafelyUnguessable ? successClass : errorClass" />
          <span class="font-weight-bold pl-10">No common passwords allowed</span>
        </div>
        <div class="condition-case mt-5 pl-4">
          <span class="font-italic text-red">{{ pwdSecureSuggestion }}</span>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, WritableComputedRef } from 'vue';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';

const successClass = 'fa fa-check text-success';
const errorClass = 'fa fa-close text-danger';

const options = {
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
};

zxcvbnOptions.setOptions(options);

const props = defineProps({
  oldPassword: {
    type: String,
    default: '',
  },
  password: {
    type: String,
    default: '',
  },
  confirmPassword: {
    type: String,
    default: '',
  },
  rowClass: {
    type: String,
    default: 'row align-items-center',
  },
  lblClass: {
    type: String,
    default: 'col-sm-3',
  },
  fieldClass: {
    type: String,
    default: 'col-sm-8',
  },
  required: {
    type: Boolean,
    default: true,
  },
  showOldPassword: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:oldPassword', 'update:password', 'update:confirmPassword', 'update-validation']);

const oldPasswordValue: WritableComputedRef<any> = computed({
  get() {
    return props.oldPassword;
  },
  set(value) {
    emit('update:oldPassword', value);
  },
});

const passwordValue: WritableComputedRef<any> = computed({
  get() {
    return props.password;
  },
  set(value) {
    emit('update:password', value);
  },
});

const confirmPasswordValue: WritableComputedRef<any> = computed({
  get() {
    return props.confirmPassword;
  },
  set(value) {
    emit('update:confirmPassword', value);
  },
});

const isPasswordMatched = computed(() => passwordValue.value === confirmPasswordValue.value);
const isLengthCorrect = computed(() => passwordValue.value.length >= 15);
const isIncludeUppercase = computed(() => /[A-Z]/.test(passwordValue.value));
const isIncludeLowercase = computed(() => /[a-z]/.test(passwordValue.value));
const isIncludeNumber = computed(() => /\d/.test(passwordValue.value));
const isNotIncludeSpace = computed(() => passwordValue.value && !passwordValue.value.includes(' '));

// https://zxcvbn-ts.github.io/zxcvbn/guide/getting-started/#output
const pwdSecureResult = computed(() => zxcvbn(passwordValue.value));

const isSafelyUnguessable = computed(() => pwdSecureResult.value.score > 2);

const pwdSecureSuggestion = computed(() => {
  if (isSafelyUnguessable.value) {
    return '';
  }

  if (!pwdSecureResult.value.feedback) {
    return '';
  }

  if (pwdSecureResult.value.feedback.suggestions.length === 0 && !pwdSecureResult.value.feedback.warning) {
    return '';
  }

  return pwdSecureResult.value.feedback.warning || pwdSecureResult.value.feedback.suggestions[0];
});

const isValid = computed(() => {
  if (!props.required && !passwordValue.value) {
    return true;
  }

  return (
    isPasswordMatched.value &&
    isLengthCorrect.value &&
    isIncludeNumber.value &&
    isIncludeUppercase.value &&
    isIncludeLowercase.value &&
    isNotIncludeSpace.value &&
    isSafelyUnguessable.value
  );
});

const updateValidation = () => {
  emit('update:password', passwordValue.value);
  emit('update:confirmPassword', confirmPasswordValue.value);
  emit('update-validation', isValid.value);
};
</script>

<style lang="scss">
.password-fields-container {
  i {
    width: 14px;
  }
}
</style>
